import { useState, useEffect, type FC } from 'react'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import classNames from 'classnames'
import { type HeaderProps } from './Header.interface'
import { Icon } from '~/components/Icon/Icon'
import { Button } from '../Button/Button'
import { type SearchOverlayProps } from '../SearchOverlay/SearchOverlay.interface'
import styles from './Header.module.scss'

interface ExtendedHeaderProps extends HeaderProps {
  altCTA?: boolean;
}

export const Header: FC<ExtendedHeaderProps> = ({
  searchText,
  cta,
  pink,
  ctaActive,
  searchOverlay,
  altCTA = false
}) => {
  const SearchOverlay = dynamic<SearchOverlayProps>(
    async () => await import('~/components/SearchOverlay/SearchOverlay').then((mod) => mod.SearchOverlay)
  )

  const [activeOverlay, setActiveOverlay] = useState<string | null>(null)

  const toggleOverlay = (
    e: React.MouseEvent<HTMLButtonElement> | null,
    id: string
  ): void => {
    if (e) {
      e.preventDefault()
    }

    if (activeOverlay === id) {
      setActiveOverlay(null)
      return
    }
    setActiveOverlay(id)
  }

  useEffect(() => {
    const htmlEl = document.querySelector('html')

    if (htmlEl) {
      if (activeOverlay) {
        document.body.style.top = `-${window.scrollY}px`
        htmlEl.classList.add('html-nav-open')
      } else if (htmlEl.classList.contains('html-nav-open')) {
        htmlEl.classList.remove('html-nav-open')
      }
    }
  }, [activeOverlay])

  return (
    <header
      className={`${styles.container} ${pink && styles['container--pink']}`}
    >
      <div className={styles.grid}>
        <div className={styles['sub-grid']}>
          <div className={styles['logo-container']}>
            <Link href="/" title="Indie Witney" className={styles.logo}>
              <Icon name="#logo-full" className={styles['logo-desktop']} />
              <Icon name="#logo-round" className={styles['logo-mobile']} />
            </Link>
          </div>
          <div className={styles['search-container']}>
            {searchOverlay && (
            <Button
              variant="secondary"
              round
              className={`${styles.button} ${activeOverlay === 'search' ? styles['button--close'] : undefined}`}
              ariaLabel={searchText}
              onClick={(e) => {
                toggleOverlay(e, 'search')
              }}
              id="searchButton"
            >
              <span className={styles['search-text']}>{searchText}</span>
              <Icon
                name={activeOverlay === 'search' ? '#search-cross' : '#search'}
                className={`${styles.icon} ${activeOverlay === 'search' ? styles['icon--close'] : undefined}`}
              />
            </Button>
            )}
          </div>
        </div>
        {cta && (
        <Link
          href={cta.link}
          className={classNames(styles.cta, {
            [styles['cta--active']]: ctaActive,
            [styles['alt-cta']]: altCTA
          })}
        >
          <span className={styles['cta-small']}>{cta.textOne}</span>
          <span className={styles['cta-large']}>{cta.textTwo}</span>
        </Link>
        )}
      </div>
      {searchOverlay && (
        <SearchOverlay
          closeOverlay={toggleOverlay}
          show={activeOverlay === 'search'}
          title={searchOverlay.title}
          typeTitle={searchOverlay.typeTitle}
          categoryTitle={searchOverlay.categoryTitle}
          businessTypes={searchOverlay.businessTypes}
          searchButton={searchOverlay.searchButton}
        />
      )}
    </header>
  )
}
